import type { RefObject } from 'react';
import { useEffect } from 'react';

import { PIN_CLEARANCE_VAR, PIN_OFFSET_VAR } from '../ScenarioGrid.style';

export const PINNED_HEADER_SELECTOR = '[data-pinned-header]';

export const usePinnedHeaderOffset = (gridRef: RefObject<HTMLElement>, pinTop: number) => {
    useEffect(() => {
        const grid = gridRef.current;
        if (!grid) {
            return undefined;
        }

        let frame = 0;
        let offset = 0;
        let clearance = 0;

        const update = () => {
            frame = 0;
            const pinned = grid.querySelector<HTMLElement>(PINNED_HEADER_SELECTOR);
            if (!pinned) {
                return;
            }
            const gridRect = grid.getBoundingClientRect();
            const pinnedRect = pinned.getBoundingClientRect();
            const naturalTop = pinnedRect.top - offset;
            const maxOffset = Math.max(0, gridRect.bottom - naturalTop - pinnedRect.height);
            const next = Math.min(Math.max(0, pinTop - naturalTop), maxOffset);

            const nextClearance = pinTop + pinnedRect.height;

            if (next !== offset) {
                offset = next;
                grid.style.setProperty(PIN_OFFSET_VAR, `${next}px`);
            }
            if (nextClearance !== clearance) {
                clearance = nextClearance;
                grid.style.setProperty(PIN_CLEARANCE_VAR, `${nextClearance}px`);
            }
        };

        const schedule = () => {
            if (!frame) {
                frame = requestAnimationFrame(update);
            }
        };

        const resizeObserver = new ResizeObserver(schedule);
        resizeObserver.observe(grid);
        window.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('resize', schedule);
        schedule();

        return () => {
            cancelAnimationFrame(frame);
            resizeObserver.disconnect();
            window.removeEventListener('scroll', schedule);
            window.removeEventListener('resize', schedule);
            grid.style.removeProperty(PIN_OFFSET_VAR);
            grid.style.removeProperty(PIN_CLEARANCE_VAR);
        };
    }, [gridRef, pinTop]);
};
